import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Title, Text, Button, Card } from '@tremor/react'
import { UserGroupIcon, SparklesIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import CharacterizationFormModal from './CharacterizationFormModal'
import CharacterizationAIGenerationModal from './CharacterizationAIGenerationModal'
import ConfirmationModal from '../ConfirmationModal'
import { 
    getProjectCharacterizations, 
    createProjectCharacterization, 
    updateProjectCharacterization, 
    deleteProjectCharacterization,
    generateAICharacterization,
    setActiveCharacterization 
} from '../../Requests/CharacterizationRequests'

function CaracterizacaoTab({ projectUuid, existingResearchContext = {}, onNavigateNext }) {
    const { t } = useTranslation()
    const [characterizations, setCharacterizations] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedCharacterization, setSelectedCharacterization] = useState(null)
    const [formModal, setFormModal] = useState(false)
    const [aiModal, setAiModal] = useState(false)
    const [editCharacterization, setEditCharacterization] = useState(null)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [characterizationToDelete, setCharacterizationToDelete] = useState(null)

    useEffect(() => {
        if (projectUuid) {
            loadCharacterizations()
        }
    }, [projectUuid])

    const loadCharacterizations = async () => {
        try {
            setLoading(true)
            const response = await getProjectCharacterizations(projectUuid)
            setCharacterizations(response.data)
        } catch (error) {
            console.error('Error loading characterizations:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleCreateManual = () => {
        setEditCharacterization(null)
        setFormModal(true)
    }

    const handleCreateAI = () => {
        setAiModal(true)
    }

    const handleEdit = (characterization) => {
        setEditCharacterization(characterization)
        setFormModal(true)
    }

    const handleSubmitCharacterization = async (data) => {
        try {
            setSubmitLoading(true)
            if (editCharacterization) {
                await updateProjectCharacterization(projectUuid, editCharacterization.uuid, data)
            } else {
                await createProjectCharacterization(projectUuid, data)
            }
            await loadCharacterizations()
            setFormModal(false)
        } catch (error) {
            console.error('Error saving characterization:', error)
            throw error
        } finally {
            setSubmitLoading(false)
        }
    }

    const handleSubmitAIGeneration = async (responses) => {
        try {
            setSubmitLoading(true)
            const response = await generateAICharacterization(projectUuid, responses)
            await loadCharacterizations()
            setAiModal(false)
        } catch (error) {
            console.error('Error generating AI characterization:', error)
            throw error
        } finally {
            setSubmitLoading(false)
        }
    }

    const handleSelect = async (characterization) => {
        try {
            setSubmitLoading(true)
            await setActiveCharacterization(projectUuid, characterization.uuid)
            setSelectedCharacterization(characterization)
            await loadCharacterizations() // Refresh to show updated status
            
            // Navigate to next step after selecting a characterization
            if (onNavigateNext) {
                setTimeout(() => onNavigateNext(), 500) // Small delay for UX
            }
        } catch (error) {
            console.error('Error selecting characterization:', error)
        } finally {
            setSubmitLoading(false)
        }
    }

    const handleDeleteClick = (characterizationUuid) => {
        setCharacterizationToDelete(characterizationUuid)
        setShowDeleteModal(true)
    }

    const handleDeleteConfirm = async () => {
        if (!characterizationToDelete) return

        try {
            await deleteProjectCharacterization(projectUuid, characterizationToDelete)
            setCharacterizations(prev => prev.filter(c => c.uuid !== characterizationToDelete))
            setShowDeleteModal(false)
            setCharacterizationToDelete(null)
        } catch (err) {
            console.error('Failed to delete characterization:', err)
            alert(err.error || t('Research.Caracterizacao.DeleteError'))
        }
    }

    const renderEmptyState = () => (
        <div data-testid="caracterizacao-empty" className="bg-gray-50 rounded-lg p-8 text-center">
            <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <Text className="text-gray-500 mb-2 text-lg">
                {t('Research.Caracterizacao.EmptyState')}
            </Text>
            <Text className="text-gray-400 mb-6 text-sm">
                {t('Research.Caracterizacao.EmptyStateHint')}
            </Text>
            <div className="flex justify-center gap-4">
                <Button
                    onClick={handleCreateManual}
                    icon={PencilIcon}
                    className="bg-white border border-iavisus-100 text-iavisus-100 hover:bg-iavisus-50"
                >
                    {t('Research.Caracterizacao.CreateManual')}
                </Button>
                <Button
                    onClick={handleCreateAI}
                    icon={SparklesIcon}
                    className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                >
                    {t('Research.Caracterizacao.GenerateAI')}
                </Button>
            </div>
        </div>
    )

    const renderCharacterization = (characterization) => (
        <Card key={characterization.id} data-testid={`caracterizacao-item-${characterization.uuid || characterization.id}`} className="p-4">
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${
                            characterization.type === 'ai_generated' ? 'bg-iavisus-100' : 'bg-blue-500'
                        }`}></span>
                        <Text className="text-xs text-gray-500">
                            {characterization.type === 'ai_generated' ? 
                                t('Research.Caracterizacao.AIGenerated') : 
                                t('Research.Caracterizacao.ManualType')
                            }
                        </Text>
                        {characterization.status === 'active' && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-iavisus-900 text-white">
                                {t(`Research.Caracterizacao.Status.active`)}
                            </span>
                        )}
                    </div>
                    
                    <div className="mb-3">
                        <Text className="font-medium text-gray-900 mb-1">{t('Research.Caracterizacao.Universe')}:</Text>
                        <Text className="text-gray-700">
                            {characterization.universe || t('Research.Caracterizacao.EmptyDefinition')}
                        </Text>
                    </div>

                    <div className="mb-3">
                        <Text className="font-medium text-gray-900 mb-1">{t('Research.Caracterizacao.Population')}:</Text>
                        <Text className="text-gray-700">
                            {characterization.population || t('Research.Caracterizacao.EmptyDefinition')}
                        </Text>
                    </div>

                    <div className="mb-3">
                        <Text className="font-medium text-gray-900 mb-1">{t('Research.Caracterizacao.Sample')}:</Text>
                        <Text className="text-gray-700">
                            {characterization.sample || t('Research.Caracterizacao.EmptyDefinition')}
                        </Text>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                            <Text className="font-medium text-gray-900 mb-1">{t('Research.Caracterizacao.SamplingType')}:</Text>
                            <Text className="text-gray-700">
                                {characterization.sampling_type || t('Research.Caracterizacao.EmptyDefinition')}
                            </Text>
                        </div>
                        <div>
                            <Text className="font-medium text-gray-900 mb-1">{t('Research.Caracterizacao.SampleSize')}:</Text>
                            <Text className="text-gray-700">
                                {characterization.sample_size || t('Research.Caracterizacao.EmptyDefinition')}
                            </Text>
                        </div>
                    </div>

                    {characterization.ai_reasoning && (
                        <div className="mb-3">
                            <Text className="font-medium text-gray-900 mb-1">{t('Research.Caracterizacao.ShowReasoning')}:</Text>
                            <Text className="text-gray-700 italic">
                                {characterization.ai_reasoning}
                            </Text>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-1 text-xs text-gray-500 mt-3">
                        <span>{t('Research.Caracterizacao.CreatedBy')} {characterization.user?.name || t('Common.Unknown')}</span>
                        <span>{new Date(characterization.created_at).toLocaleDateString()}</span>
                        {characterization.credits_used > 0 && (
                            <span>{characterization.credits_used} {t('Common.Credits')}</span>
                        )}
                    </div>
                </div>
                <div className="flex space-x-2 ml-4">
                    {characterization.status !== 'active' && (
                        <button
                            onClick={() => handleSelect(characterization)}
                            disabled={submitLoading}
                            className="px-3 py-1 text-sm bg-iavisus-100 text-white rounded-md hover:bg-iavisus-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t('Research.Caracterizacao.ClickToSelect')}
                        >
                            {submitLoading ? t('Common.Loading') : t('Research.Caracterizacao.Select')}
                        </button>
                    )}
                    <button
                        onClick={() => handleEdit(characterization)}
                        disabled={submitLoading}
                        className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t('Common.Edit')}
                    >
                        <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => handleDeleteClick(characterization.uuid)}
                        className="p-1 text-red-500 hover:text-red-700"
                        title={t('Common.Delete')}
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </Card>
    )

    return (
        <div data-testid="caracterizacao-container" className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <UserGroupIcon className="h-6 w-6 text-iavisus-100" />
                    <Title className="text-lg font-semibold">
                        {t('Research.Caracterizacao.Title')}
                    </Title>
                </div>

                <div className="flex space-x-2">
                    <Button
                        data-testid="caracterizacao-create-btn"
                        onClick={handleCreateManual}
                        icon={PencilIcon}
                        size="sm"
                        className="bg-white border border-iavisus-100 text-iavisus-100 hover:bg-iavisus-50"
                    >
                        {t('Research.Caracterizacao.CreateManual')}
                    </Button>
                    <Button
                        data-testid="caracterizacao-ai-btn"
                        onClick={handleCreateAI}
                        icon={SparklesIcon}
                        size="sm"
                        className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                    >
                        {t('Research.Caracterizacao.GenerateAI')}
                    </Button>
                </div>
            </div>
            
            <Text className="text-gray-600">
                {t('Research.Caracterizacao.Description')}
            </Text>

            {loading ? (
                <div className="flex justify-center py-8">
                    <Text className="text-gray-500">{t('Common.Loading')}</Text>
                </div>
            ) : characterizations.length === 0 ? (
                renderEmptyState()
            ) : (
                <div className="space-y-4">
                    {characterizations.map(renderCharacterization)}
                </div>
            )}

            {/* Modals */}
            <CharacterizationFormModal
                isOpen={formModal}
                onClose={() => setFormModal(false)}
                onSubmit={handleSubmitCharacterization}
                editCharacterization={editCharacterization}
                loading={submitLoading}
            />

            <CharacterizationAIGenerationModal
                isOpen={aiModal}
                onClose={() => setAiModal(false)}
                onSubmit={handleSubmitAIGeneration}
                loading={submitLoading}
                existingResearchContext={existingResearchContext}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                open={showDeleteModal}
                setOpen={setShowDeleteModal}
                title={t('Common.Delete')}
                message={t('Research.Caracterizacao.DeleteConfirm')}
                confirmText={t('Common.Delete')}
                cancelText={t('Common.Cancel')}
                variant="danger"
                onConfirm={handleDeleteConfirm}
            />
        </div>
    )
}

export default CaracterizacaoTab